import { assign, cloneDeep, isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom-v5-compat';

import { showToast } from '~/components/Shared/EcToast/showToast';
import PageTitle from '~/components/Shared/PageTitle/PageTitle';
import { Box } from '~/eds/src/components/Box/Box';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { EmptyPage } from '~/eds/src/components/EmptyPage/EmptyPage';
import { useDeleteModal } from '~/eds/src/components/hooks/useDeleteModal';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { Tree } from '~/eds/src/components/Tree/Tree';
import { TreeNode } from '~/eds/src/types/TreeNode';
import { UserAction } from '~/eds/src/types/UserAction';
import { scrollElementIntoView } from '~/eds/src/utils/scrollElementIntoView';
import { ClauseLibraryViewType } from '~/enums/ClauseLibraryViewType';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { api } from '~/redux/api/createApi';
import { clauseLibraryGetClause } from '~/redux/api/methods/clauseLibrary';
import { slices } from '~/redux/slices';
import { initialState } from '~/redux/slices/clauseLibrary';
import { useRouting } from '~/routing/useRouting';
import { ERROR, SUCCESS } from '~/types/toast.types';
import { Uuid } from '~/types/Uuid';
import { sortStrings } from '~/utils/sortStrings';
import { testIsSuperAdmin } from '~/utils/user';

import ClauseFilter from './ClauseFilter/ClauseFilter';
import ClauseLibraryManagement from './ClauseManagement/ClauseLibraryManagement';
import { sortVariationsByPositionAndName } from './utils';

interface ClauseLibraryProps {
  hasEditPermission: boolean;
}

const ClauseLibrary = ({ hasEditPermission }: ClauseLibraryProps) => {
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const intl = useIntl();
  const formatDate = useFormatDate();
  const { location, params } = useRouting();
  const { hash } = location;
  const { navigate } = useRouting();
  const { clauseId } = params as { clauseId: string };

  const filters = useSelector(slices.clauseLibrary.selectors.selectFilters);
  const clauseSummaries = useSelector(
    slices.clauseLibrary.selectors.selectClauseSummaries,
  );
  const clauseForm = useSelector(
    slices.clauseLibrary.selectors.selectClauseForm,
  );
  const selectedClause = useSelector(
    slices.clauseLibrary.selectors.selectSelectedClause,
  );

  const [idToBeDeleted, setIdToBeDeleted] = useState<Uuid>();
  const [nameToBeDeleted, setNameToBeDeleted] = useState<string>();
  const [idForURL, setIdForURL] = useState<Uuid>();

  const canEdit = testIsSuperAdmin(currentUser) || hasEditPermission;

  const editText = intl.formatMessage({
    id: 'ClauseLibrary.editAction',
    defaultMessage: 'Edit',
    description: 'Edit action text',
  });

  const deleteText = intl.formatMessage({
    id: 'ClauseLibrary.deleteAction',
    defaultMessage: 'Delete',
    description: 'Delete action text',
  });

  const addText = intl.formatMessage({
    id: 'ClauseLibrary.addClauseAction',
    defaultMessage: 'Add Clause',
    description: 'Add clause action text',
  });
  const {
    data: clauseSummariesData,
    error: clauseSummariesError,
    isFetching: clauseSummariesLoading,
  } = api.endpoints.clauseLibraryListClauses.useQuery({ filters });

  const [deleteClause, deleteClauseResult] =
    api.endpoints.clauseLibraryDeleteClause.useMutation();
  const { isError: isDeleteClauseError, isSuccess: isDeleteClauseSuccess } =
    deleteClauseResult;

  const [deleteVariation, deleteVariationResult] =
    api.endpoints.clauseLibraryDeleteVariation.useMutation();
  const {
    isError: isDeleteVariationError,
    isSuccess: isDeleteVariationSuccess,
  } = deleteVariationResult;

  const postDeleteClause = () => {
    navigate('/clause-library');
  };

  const postDeleteVariation = () => {
    navigate(`/clause-library/${idForURL}`);
  };

  const updateClauseSummaries = (
    apiData: TreeNode[],
    initialData: TreeNode[],
  ): TreeNode[] => {
    const result: TreeNode[] = [];
    [...apiData].forEach((clauseSummaryData) => {
      const matchingClause = [...initialData].find(
        (clause) => clauseSummaryData.key === clause.key,
      );
      if (!!matchingClause) {
        const clauseResult = assign(
          {},
          { ...matchingClause },
          { ...clauseSummaryData },
        );
        if (clauseSummaryData.children?.length) {
          const children: TreeNode[] = [];
          clauseSummaryData.children.forEach((variationData) => {
            const matchingVariation = matchingClause.children?.find(
              (variation) => variationData.key === variation.key,
            );
            if (!!matchingVariation) {
              const variationResult = assign(
                {},
                { ...matchingVariation },
                { ...variationData },
              );
              children.push(variationResult);
            } else {
              children.push(variationData);
            }
          });
          children.sort(sortVariationsByPositionAndName);
          clauseResult.children = children;
        }
        result.push(clauseResult);
      } else {
        result.push(clauseSummaryData);
      }
    });
    return result?.sort((a: TreeNode, b: TreeNode) =>
      sortStrings(a.name, b.name),
    );
  };

  useEffect(() => {
    if (!!clauseId && clauseSummariesData) {
      const selected: TreeNode | undefined = clauseSummariesData?.find(
        (clause) => clause.key === clauseId,
      );
      dispatch(slices.clauseLibrary.actions.setSelectedClause(selected));
      if (!selected) {
        navigate('/clause-library');
      } else if (hash) {
        scrollElementIntoView(hash.slice(1));
      }
    }

    if (!!clauseSummariesData) {
      const summaries = getClauseSummaries(clauseSummariesData);
      const result = updateClauseSummaries(summaries, clauseSummaries);
      dispatch(slices.clauseLibrary.actions.setClauseSummaries(result));
    }

    return () => {
      dispatch(slices.clauseLibrary.actions.cleanupClauseSummaries());
    };
  }, [clauseSummariesData, navigate]);

  useEffect(() => {
    if (clauseSummariesError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'ClauseLibrary.loadClausesError',
          defaultMessage: 'Failed to load clauses',
          description: 'Error message when clauses fail to load',
        }),
      );
    }
  }, [clauseSummariesError]);

  useEffect(() => {
    if (isDeleteClauseSuccess) {
      hideDeleteClauseModal();
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'ClauseLibrary.deleteClauseSuccess',
            defaultMessage: 'Successfully deleted: {nameToBeDeleted}.',
            description:
              'Success message when clause is deleted. Example: nameToBeDeleted = "Termination Clause"',
          },
          { nameToBeDeleted },
        ),
      );
      postDeleteClause();
    }
    if (isDeleteClauseError) {
      showToast(
        ERROR,
        intl.formatMessage(
          {
            id: 'ClauseLibrary.deleteClauseError',
            defaultMessage: 'Failed to delete: {nameToBeDeleted}.',
            description:
              'Error message when clause deletion fails. Example: nameToBeDeleted = "Termination Clause"',
          },
          { nameToBeDeleted },
        ),
      );
    }
  }, [isDeleteClauseError, isDeleteClauseSuccess]);

  useEffect(() => {
    if (isDeleteVariationSuccess) {
      hideDeleteVariationModal();
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'ClauseLibrary.deleteVariationSuccess',
            defaultMessage: 'Successfully deleted: {nameToBeDeleted}.',
            description:
              'Success message when variation is deleted. Example: nameToBeDeleted = "Variation A"',
          },
          { nameToBeDeleted },
        ),
      );
      postDeleteVariation();
    }
    if (isDeleteVariationError) {
      showToast(
        ERROR,
        intl.formatMessage(
          {
            id: 'ClauseLibrary.deleteVariationError',
            defaultMessage: 'Failed to delete: {nameToBeDeleted}.',
            description:
              'Error message when variation deletion fails. Example: nameToBeDeleted = "Variation A"',
          },
          { nameToBeDeleted },
        ),
      );
    }
  }, [isDeleteVariationError, isDeleteVariationSuccess]);

  const handleCreateClause = () => {
    dispatch(
      slices.clauseLibrary.actions.setClauseForm({
        ...clauseForm,
        name: '',
      }),
    );
    dispatch(
      slices.clauseLibrary.actions.setView(ClauseLibraryViewType.ClauseForm),
    );
  };

  const handleEditClause = async (clauseId: Uuid) => {
    const { name, guidance: guidanceNotes } = await clauseLibraryGetClause({
      clauseId,
    });
    dispatch(
      slices.clauseLibrary.actions.setClauseForm({
        title: name,
        name,
        guidanceNotes,
        clauseId,
      }),
    );
    dispatch(slices.clauseLibrary.actions.setEditMode(true));
    dispatch(
      slices.clauseLibrary.actions.setView(ClauseLibraryViewType.ClauseForm),
    );
  };

  const handleDeleteClause = (clauseId: Uuid, clauseName: string) => {
    setIdToBeDeleted(clauseId);
    setNameToBeDeleted(clauseName);
    showDeleteClauseModal();
  };

  const handleCreateClauseVariation = (clauseId: Uuid, clauseName: string) => {
    const timestamp = formatDate(new Date(), 'utc');
    const namePlaceholder = `${clauseName}_${timestamp}`;
    dispatch(
      slices.clauseLibrary.actions.setClauseForm({
        ...initialState.clauseForm,
        name: clauseName,
      }),
    );
    dispatch(
      slices.clauseLibrary.actions.setVariationForm({
        ...initialState.variationForm,
        clauseId,
        namePlaceholder,
      }),
    );
    dispatch(
      slices.clauseLibrary.actions.setView(ClauseLibraryViewType.VariationForm),
    );
  };

  const renderRightEmptyContent = () => {
    if (clauseSummariesLoading || clauseId) return null;
    if (!!clauseSummaries?.length) {
      return <EmptyPage preset="no-clauses-admin" />;
    }
    return (
      <EmptyPage
        preset={
          !!filters?.query || !!filters?.chips
            ? 'no-matches'
            : canEdit
              ? 'no-clauses-admin'
              : 'no-clauses-user'
        }
      />
    );
  };

  const handleEditVariation = async (clauseId: Uuid, variationId: Uuid) => {
    const clause = await clauseLibraryGetClause({ clauseId });
    const variation = clause?.variations?.find(
      (variation) => variation.key === variationId,
    );
    const namePlaceholder = clause.name + '_' + formatDate(new Date(), 'utc');
    dispatch(
      slices.clauseLibrary.actions.setVariationForm({
        clauseId,
        variationId: variation?.key,
        name: variation?.name ?? '',
        title: variation?.name,
        position: variation?.position,
        guidanceNotes: variation?.guidance ?? '',
        clauseText: variation?.text ?? '',
        namePlaceholder,
      }),
    );
    dispatch(slices.clauseLibrary.actions.setEditMode(true));
    dispatch(
      slices.clauseLibrary.actions.setView(ClauseLibraryViewType.VariationForm),
    );
  };

  const handleDeleteVariation = (
    variationId: Uuid,
    variationName: string,
    clauseId: Uuid,
  ) => {
    setIdToBeDeleted(variationId);
    setNameToBeDeleted(variationName);
    setIdForURL(clauseId);
    showDeleteVariationModal();
  };

  const handleTreeUpdate = (updatedData: TreeNode[]) => {
    if (!isEqual(updatedData, clauseSummaries)) {
      dispatch(slices.clauseLibrary.actions.setClauseSummaries(updatedData));
    }
  };

  const [deleteClauseModal, showDeleteClauseModal, hideDeleteClauseModal] =
    useDeleteModal({
      children: (
        <Layout direction="column" spacing={4}>
          <Text>
            <FormattedMessage
              id="ClauseLibrary.deleteClauseWarning"
              defaultMessage="Deleting <strong>{nameToBeDeleted}</strong> will delete all its variations. This action is not reversible."
              description="Warning message when deleting a clause. Example: nameToBeDeleted = 'Termination Clause'"
              values={{
                nameToBeDeleted,
                strong: (chunks) => <strong>{chunks}</strong>,
              }}
            />
          </Text>
        </Layout>
      ),
      title: intl.formatMessage(
        {
          id: 'ClauseLibrary.deleteClauseTitle',
          defaultMessage: 'Delete {nameToBeDeleted}',
          description:
            'Title for delete clause modal. Example: nameToBeDeleted = "Termination Clause"',
        },
        { nameToBeDeleted },
      ),
      primaryActionText: intl.formatMessage({
        id: 'ClauseLibrary.deleteClauseButton',
        defaultMessage: 'Delete Clause',
        description: 'Delete clause button text',
      }),
      onDelete: () => deleteClause({ clauseId: idToBeDeleted ?? '' }),
    });

  const [
    deleteVariationModal,
    showDeleteVariationModal,
    hideDeleteVariationModal,
  ] = useDeleteModal({
    children: (
      <Layout direction="column" spacing={4}>
        <Text>
          {intl.formatMessage({
            id: 'ClauseLibrary.deleteVariationWarning',
            defaultMessage: 'Deleting a clause variation is not reversible',
            description: 'Warning message when deleting a variation',
          })}
        </Text>
      </Layout>
    ),
    title: intl.formatMessage({
      id: 'ClauseLibrary.deleteVariationTitle',
      defaultMessage: 'Delete clause variation?',
      description: 'Title for delete variation modal',
    }),
    primaryActionText: intl.formatMessage({
      id: 'ClauseLibrary.deleteVariationButton',
      defaultMessage: 'Delete Clause Variation',
      description: 'Delete variation button text',
    }),
    onDelete: () => deleteVariation({ variationId: idToBeDeleted as Uuid }),
  });

  // Mapping can't occur in helper function, clause actions use redux unavailable outside components
  const getClauseSummaries = (summaryData: TreeNode[]): TreeNode[] =>
    summaryData?.map((clause): TreeNode => {
      // clause is not extensible, necessitating deep copy
      const clauseSummary = cloneDeep(clause);
      clauseSummary.actions = canEdit
        ? [
            {
              mode: 'icon',
              text: intl.formatMessage({
                id: 'ClauseLibrary.addVariationAction',
                defaultMessage: 'Add Clause Variation',
                description: 'Add clause variation action text',
              }),
              icon: 'plus',
              onClick: () =>
                handleCreateClauseVariation(clause.key, clause.name),
            },
          ]
        : [];
      clauseSummary.moreActions = canEdit
        ? [
            {
              text: editText,
              id: clause.key,
              onClick: () => handleEditClause(clause.key),
            },
            {
              text: deleteText,
              id: clause.key,
              onClick: () => handleDeleteClause(clause.key, clause.name),
            },
          ]
        : [];
      clauseSummary.children?.forEach((variation) => {
        variation.moreActions = canEdit
          ? [
              {
                text: editText,
                id: variation.key,
                onClick: () => handleEditVariation(clause.key, variation.key),
              },
              {
                text: deleteText,
                id: variation.key,
                onClick: () =>
                  handleDeleteVariation(
                    variation.key,
                    variation.name,
                    clause.key,
                  ),
              },
            ]
          : [];
      });
      clauseSummary.children?.sort(sortVariationsByPositionAndName);
      return clauseSummary;
    });

  const summaryHeaderActions: UserAction[] = canEdit
    ? [
        {
          mode: 'icon',
          text: addText,
          tooltip: addText,
          icon: 'plus',
          onClick: handleCreateClause,
        },
      ]
    : [];

  return (
    <Layout direction="column" spacing={8}>
      <PageTitle
        title={intl.formatMessage({
          id: 'ClauseLibrary.pageTitle',
          defaultMessage: 'Clause Library',
          description: 'Page title for clause library',
        })}
      />
      <ClauseFilter />
      <ClauseLibraryManagement />
      <Layout
        direction="row"
        flex="auto"
        mx={-8}
        mb={-6}
        borderTop="border.divider"
        h="calc(100vh - 234px)"
      >
        <Box
          flex="none"
          w="25%"
          minW="sidebar.width.s"
          maxW="sidebar.width.m"
          borderRight="border.divider"
        >
          {!clauseSummariesLoading && (
            <Tree
              expandSelectedOnSelect={false}
              name={intl.formatMessage({
                id: 'ClauseLibrary.clausesTreeName',
                defaultMessage: 'Clauses',
                description: 'Name for clauses tree component',
              })}
              tree={clauseSummaries}
              title={intl.formatMessage({
                id: 'ClauseLibrary.clausesTreeTitle',
                defaultMessage: 'Clauses',
                description: 'Title for clauses tree component',
              })}
              headerActions={summaryHeaderActions}
              onUpdate={handleTreeUpdate}
              selectedNodeKey={selectedClause?.key}
              emptyContent={
                <EmptyPage
                  preset={
                    !!filters?.query || !!filters?.chips
                      ? 'no-matches'
                      : canEdit
                        ? 'no-clauses-admin'
                        : 'no-clauses-user'
                  }
                />
              }
              enableExtraChildPadding={true}
            />
          )}
        </Box>
        {renderRightEmptyContent()}

        <ContentContainer
          loadingContent={{
            isLoading: clauseSummariesLoading,
          }}
        >
          <Outlet
            context={{
              editText,
              deleteText,
              hasEditPermission,
              handleCreateClauseVariation,
              handleEditClause,
              handleDeleteClause,
              handleDeleteVariation,
            }}
          />
        </ContentContainer>
      </Layout>
      {deleteClauseModal}
      {deleteVariationModal}
    </Layout>
  );
};

export default ClauseLibrary;
